import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  LiveEvent, EventRecurrence, EventOccurrence, EventFormField, EventApplication, EventParticipant,
  Streamer,
  getEvents, createEvent, updateEvent, deleteEvent, uploadEventImage, uploadOccurrenceImage,
  addEventRecurrence, deleteEventRecurrence, generateOccurrences,
  getEventOccurrences, updateEventOccurrence,
  deleteEventOccurrence, clearEventOccurrences,
  EventLaunchStatus, getEventLaunchStatus, launchEvent,
  getEventFormFields, addEventFormField, updateEventFormField, deleteEventFormField, reorderEventFormFields,
  getEventApplications, getEventApplication, createEventApplication, updateEventApplication,
  getOccurrenceParticipants, addOccurrenceParticipants, updateOccurrenceParticipant, advanceWinners,
  getStreamers,
  EventFormTemplate, getEventFormTemplates, saveEventFormTemplate,
  applyEventFormTemplate, deleteEventFormTemplate, resolveTikTokProfile,
  getProxyImageUrl,
  EventRequest, getEventRequests, acceptEventRequest, updateEventRequest, deleteEventRequest,
} from './api';

// ── Helpers ──────────────────────────────────────────────────

const DAYS_FR = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const TYPE_COLOR = { live: '#2563eb', concours: '#7c3aed' };
const TYPE_LABEL = { live: '📡 Live', concours: '🏆 Concours' };
const STATUS_COLOR: Record<string, string> = {
  upcoming: '#2563eb', active: '#16a34a', completed: '#64748b', cancelled: '#dc2626',
};
const STATUS_LABEL: Record<string, string> = {
  upcoming: 'À venir', active: 'En cours', completed: 'Terminé', cancelled: 'Annulé',
};
const APP_STATUS_LABEL: Record<string, string> = {
  pending: '⏳ En attente', active: '✅ Sélectionné', eliminated: '❌ Éliminé',
  finalist: '🥈 Finaliste', winner: '🏆 Gagnant',
};
const PART_STATUS_LABEL: Record<string, string> = {
  competing: 'En compétition', eliminated: 'Éliminé', finalist: 'Finaliste', winner: 'Gagnant',
};

function fmtDate(s: string) {
  try {
    return new Date(s + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return s; }
}

function fmtDateTime(date: string, time: string) {
  try {
    const d = new Date(date + 'T' + time);
    return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' }) + ' · ' + time;
  } catch { return date + ' ' + time; }
}

// ── Composants réutilisables ─────────────────────────────────

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px', borderRadius: 8,
  border: '1px solid #e2e8f0', background: '#fff',
  color: '#0f172a', fontSize: 13, boxSizing: 'border-box', outline: 'none',
};
const labelStyle: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 };
const sectionTitle: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 12 };

function Btn({ children, onClick, color = '#2563eb', disabled, style, size = 'md' }: {
  children: React.ReactNode; onClick?: () => void; color?: string;
  disabled?: boolean; style?: React.CSSProperties; size?: 'sm' | 'md';
}) {
  const p = size === 'sm' ? '5px 10px' : '8px 16px';
  const fs = size === 'sm' ? 11 : 13;
  return (
    <button onClick={onClick} disabled={disabled} style={{ padding: p, borderRadius: 7, border: 'none', background: disabled ? '#e2e8f0' : color, color: disabled ? '#94a3b8' : '#fff', fontSize: fs, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer', ...style }}>
      {children}
    </button>
  );
}

function BtnOutline({ children, onClick, style, size = 'md' }: {
  children: React.ReactNode; onClick?: () => void; style?: React.CSSProperties; size?: 'sm' | 'md';
}) {
  const p = size === 'sm' ? '5px 10px' : '8px 16px';
  const fs = size === 'sm' ? 11 : 13;
  return (
    <button onClick={onClick} style={{ padding: p, borderRadius: 7, border: '1px solid #e2e8f0', background: '#fff', color: '#374151', fontSize: fs, cursor: 'pointer', fontWeight: 500, ...style }}>
      {children}
    </button>
  );
}

// ── Sélecteur de streamers ───────────────────────────────────

function StreamerPicker({ streamers, selected, onChange }: {
  streamers: Streamer[];
  selected: number[];
  onChange: (ids: number[]) => void;
}) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedStreamers = streamers.filter(s => selected.includes(s.id));
  const q = search.toLowerCase();
  const filtered = streamers.filter(s =>
    !selected.includes(s.id) &&
    (s.display_name.toLowerCase().includes(q) || s.username.toLowerCase().includes(q))
  );

  const remove = (id: number) => onChange(selected.filter(x => x !== id));
  const add = (id: number) => { onChange([...selected, id]); setSearch(''); };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Chips des streamers sélectionnés */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: selectedStreamers.length ? 10 : 0 }}>
        {selectedStreamers.map(s => (
          <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 20, padding: '4px 10px 4px 6px' }}>
            {s.avatar_url
              ? <img src={s.avatar_url} alt="" style={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
              : <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#2563eb', color: '#fff', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{s.display_name.charAt(0).toUpperCase()}</div>
            }
            <span style={{ fontSize: 12, fontWeight: 600, color: '#2563eb' }}>{s.display_name}</span>
            <button onClick={() => remove(s.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#93c5fd', fontSize: 14, lineHeight: 1, padding: 0, marginLeft: 2 }}>×</button>
          </div>
        ))}
      </div>

      {/* Champ de recherche + bouton + */}
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder="Rechercher un streamer…"
            style={{ ...inputStyle, paddingLeft: 32 }}
          />
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 13, pointerEvents: 'none' }}>🔍</span>
        </div>
        <button onClick={() => setOpen(o => !o)} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 18, color: '#2563eb', lineHeight: 1, fontWeight: 700 }}>+</button>
      </div>

      {/* Dropdown résultats */}
      {open && (
        <div style={{ position: 'absolute', zIndex: 50, top: 'calc(100% + 4px)', left: 0, right: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.10)', maxHeight: 240, overflowY: 'auto' }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '12px 14px', color: '#94a3b8', fontSize: 13 }}>
              {search ? 'Aucun résultat' : 'Tous les streamers sont déjà sélectionnés'}
            </div>
          ) : (
            filtered.map(s => (
              <div key={s.id} onClick={() => add(s.id)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
              >
                {s.avatar_url
                  ? <img src={s.avatar_url} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                  : <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#e2e8f0', color: '#374151', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{s.display_name.charAt(0).toUpperCase()}</div>
                }
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.display_name}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>@{s.username}</div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ── Carte événement (liste gauche) ───────────────────────────

function EventCard({ ev, selected, onClick }: { ev: LiveEvent; selected: boolean; onClick: () => void }) {
  const color = TYPE_COLOR[ev.event_type as keyof typeof TYPE_COLOR] || '#64748b';
  return (
    <div onClick={onClick} style={{
      background: '#fff', borderRadius: 10, overflow: 'hidden', cursor: 'pointer', marginBottom: 8,
      border: selected ? `2px solid ${color}` : '1px solid #e2e8f0',
      boxShadow: selected ? `0 0 0 3px ${color}22` : '0 1px 2px rgba(0,0,0,0.04)',
      display: 'flex', flexDirection: 'column', flexShrink: 0, transition: 'all 0.12s',
    }}>
      {ev.image_url ? (
        <img src={ev.image_url} alt="" loading="lazy"
          style={{ width: '100%', height: 130, objectFit: 'cover', display: 'block', background: '#f1f5f9' }} />
      ) : (
        <div style={{ width: '100%', height: 90, background: `${color}1f`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 38 }}>
          {ev.event_type === 'concours' ? '🏆' : '📡'}
        </div>
      )}
      <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', lineHeight: 1.3, wordBreak: 'break-word' }}>{ev.title}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20, background: color + '18', color }}>
            {TYPE_LABEL[ev.event_type as keyof typeof TYPE_LABEL] || ev.event_type}
          </span>
          {(ev.recurrences?.length ?? 0) > 0 && (
            <span style={{ fontSize: 10, color: '#64748b', padding: '2px 7px', background: '#f1f5f9', borderRadius: 20 }}>
              🔁 {ev.recurrences!.length} règle{ev.recurrences!.length > 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Onglet Infos ─────────────────────────────────────────────

function InfosTab({ ev, streamers, onUpdated }: { ev: LiveEvent; streamers: Streamer[]; onUpdated: (e: LiveEvent) => void }) {
  const [form, setForm] = useState({ title: ev.title, description: ev.description ?? '', event_type: ev.event_type });
  const [minParticipants, setMinParticipants] = useState<number>(ev.min_participants ?? 0);
  const parseList = (j?: string | null): string[] => { try { const v = JSON.parse(j || '[]'); return Array.isArray(v) ? v : []; } catch { return []; } };
  const [rules, setRules] = useState<string[]>(() => parseList(ev.rules_json));
  const [prizes, setPrizes] = useState<string[]>(() => parseList(ev.prizes_json));
  const [selStreamers, setSelStreamers] = useState<number[]>(ev.streamer_ids ?? []);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const imgRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleSave = async () => {
    setSaving(true); setMsg('');
    try {
      const clean = (a: string[]) => a.map(s => s.trim()).filter(Boolean);
      const updated = await updateEvent(ev.id, {
        ...form, description: form.description || null,
        streamer_ids: selStreamers,
        rules_json: JSON.stringify(clean(rules)),
        prizes_json: JSON.stringify(clean(prizes)),
        min_participants: Number.isFinite(minParticipants) ? Math.max(0, Math.floor(minParticipants)) : 0,
      });
      onUpdated(updated.data);
      setMsg('Sauvegardé ✓');
      setTimeout(() => setMsg(''), 2500);
    } catch { setMsg('Erreur'); }
    setSaving(false);
  };

  const handleImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const r = await uploadEventImage(ev.id, file);
      onUpdated({ ...ev, image_url: r.data.image_url, image_filename: r.data.image_filename });
    } catch {}
    setUploading(false);
    e.target.value = '';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <label style={labelStyle}>Titre de l'événement</label>
        <input style={inputStyle} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
      </div>
      <div>
        <label style={labelStyle}>Type d'événement</label>
        <div style={{ display: 'flex', gap: 8 }}>
          {(['live', 'concours'] as const).map(t => (
            <button key={t} onClick={() => setForm(f => ({ ...f, event_type: t }))}
              style={{ flex: 1, padding: '8px 0', borderRadius: 8, border: `2px solid ${form.event_type === t ? TYPE_COLOR[t] : '#e2e8f0'}`, background: form.event_type === t ? TYPE_COLOR[t] + '12' : '#fff', color: form.event_type === t ? TYPE_COLOR[t] : '#64748b', fontSize: 12, fontWeight: form.event_type === t ? 700 : 400, cursor: 'pointer' }}>
              {TYPE_LABEL[t]}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label style={labelStyle}>Description</label>
        <textarea style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Description de l'événement…" />
      </div>
      {form.event_type === 'concours' && (
        <>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 240 }}>
              <label style={labelStyle}>📋 Règles</label>
              {rules.length === 0 && <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 6 }}>Aucune règle.</div>}
              {rules.map((r, i) => (
                <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                  <input style={{ ...inputStyle, flex: 1 }} value={r} placeholder={`Règle ${i + 1}`}
                    onChange={e => setRules(a => a.map((x, j) => j === i ? e.target.value : x))} />
                  <button onClick={() => setRules(a => a.filter((_, j) => j !== i))} style={{ ...smallBtn, color: '#dc2626' }}>✕</button>
                </div>
              ))}
              <BtnOutline size="sm" onClick={() => setRules(a => [...a, ''])}>+ Ajouter une règle</BtnOutline>
            </div>
            <div style={{ flex: 1, minWidth: 240 }}>
              <label style={labelStyle}>🏆 À gagner</label>
              {prizes.length === 0 && <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 6 }}>Aucun lot.</div>}
              {prizes.map((p, i) => (
                <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                  <input style={{ ...inputStyle, flex: 1 }} value={p} placeholder={`Lot ${i + 1}`}
                    onChange={e => setPrizes(a => a.map((x, j) => j === i ? e.target.value : x))} />
                  <button onClick={() => setPrizes(a => a.filter((_, j) => j !== i))} style={{ ...smallBtn, color: '#dc2626' }}>✕</button>
                </div>
              ))}
              <BtnOutline size="sm" onClick={() => setPrizes(a => [...a, ''])}>+ Ajouter un gain</BtnOutline>
            </div>
          </div>
          <div>
            <label style={labelStyle}>🎯 Démarrage conditionné — nb min. de participants sélectionnés</label>
            <input type="number" min={0} style={{ ...inputStyle, maxWidth: 160 }} value={minParticipants}
              onChange={e => setMinParticipants(parseInt(e.target.value, 10) || 0)} />
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
              0 = désactivé. Sinon : aucune occurrence générée tant que ce nombre de candidatures « Active » n'est pas atteint ; à l'atteinte, le lancement est proposé à valider (onglet Planning).
            </div>
          </div>
        </>
      )}
      <div>
        <label style={labelStyle}>Image de l'événement</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {ev.image_url && <img src={ev.image_url} alt="" style={{ width: 64, height: 64, borderRadius: 8, objectFit: 'cover', border: '1px solid #e2e8f0' }} />}
          <input type="file" ref={imgRef} onChange={handleImage} accept="image/*" style={{ display: 'none' }} />
          <BtnOutline onClick={() => imgRef.current?.click()}>{uploading ? 'Upload…' : ev.image_url ? '🔄 Changer l\'image' : '📷 Ajouter une image'}</BtnOutline>
        </div>
      </div>
      <div>
        <label style={labelStyle}>Streamers assignés</label>
        <StreamerPicker streamers={streamers} selected={selStreamers} onChange={setSelStreamers} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Btn onClick={handleSave} disabled={saving}>{saving ? 'Sauvegarde…' : 'Sauvegarder'}</Btn>
        {msg && <span style={{ fontSize: 13, color: msg.includes('Erreur') ? '#dc2626' : '#16a34a' }}>{msg}</span>}
      </div>
    </div>
  );
}

// ── Onglet Planning ──────────────────────────────────────────

function PlanningTab({ ev, onUpdated }: { ev: LiveEvent; onUpdated: (e: LiveEvent) => void }) {
  const [recurrences, setRecurrences] = useState<EventRecurrence[]>(ev.recurrences ?? []);
  const [occurrences, setOccurrences] = useState<EventOccurrence[]>([]);
  const [loadingOcc, setLoadingOcc] = useState(true);
  const [generating, setGenerating] = useState<number | null>(null);
  const [addingRec, setAddingRec] = useState(false);
  const [newRec, setNewRec] = useState({ recurrence_type: 'once', day_of_week: 4, time_start: '20:00', time_end: '22:00', date_from: '', date_until: '', weeks_count: '' as string | number, end_mode: 'indefinite' as 'indefinite' | 'date' | 'weeks' });
  const [editOcc, setEditOcc] = useState<EventOccurrence | null>(null);
  const occImgRef = useRef<HTMLInputElement>(null);
  const [launch, setLaunch] = useState<EventLaunchStatus | null>(null);
  const [launchDate, setLaunchDate] = useState('');
  const [launching, setLaunching] = useState(false);

  const loadOcc = useCallback(async () => {
    setLoadingOcc(true);
    try { const r = await getEventOccurrences(ev.id); setOccurrences(r.data); } catch {}
    setLoadingOcc(false);
  }, [ev.id]);

  const loadLaunch = useCallback(async () => {
    try {
      const r = await getEventLaunchStatus(ev.id);
      setLaunch(r.data);
      if (r.data.proposed_start_date && !launchDate) setLaunchDate(r.data.proposed_start_date);
    } catch {}
  }, [ev.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadOcc(); loadLaunch(); }, [loadOcc, loadLaunch]);

  const handleLaunch = async () => {
    if (!launchDate) return;
    setLaunching(true);
    try {
      await launchEvent(ev.id, launchDate);
      await loadLaunch();
      await loadOcc();
    } catch {}
    setLaunching(false);
  };

  const condEvent = (ev.min_participants ?? 0) > 0;
  const handleAddRec = async () => {
    if (!newRec.date_from && !condEvent) return;
    try {
      const payload: any = {
        recurrence_type: newRec.recurrence_type,
        time_start: newRec.time_start,
        time_end: newRec.time_end,
        date_from: newRec.date_from || '',
      };
      if (newRec.recurrence_type === 'weekly') payload.day_of_week = newRec.day_of_week;
      if (newRec.end_mode === 'date' && newRec.date_until) payload.date_until = newRec.date_until;
      if (newRec.end_mode === 'weeks' && newRec.weeks_count) payload.weeks_count = Number(newRec.weeks_count);
      const r = await addEventRecurrence(ev.id, payload);
      const updated = [...recurrences, r.data];
      setRecurrences(updated);
      onUpdated({ ...ev, recurrences: updated });
      setAddingRec(false);
      setNewRec({ recurrence_type: 'once', day_of_week: 4, time_start: '20:00', time_end: '22:00', date_from: '', date_until: '', weeks_count: '', end_mode: 'indefinite' });
    } catch {}
  };

  const handleDeleteRec = async (recId: number) => {
    await deleteEventRecurrence(ev.id, recId);
    const updated = recurrences.filter(r => r.id !== recId);
    setRecurrences(updated);
    onUpdated({ ...ev, recurrences: updated });
    await loadOcc();
  };

  const handleGenerate = async (recId: number) => {
    setGenerating(recId);
    try { await generateOccurrences(ev.id, recId); await loadOcc(); } catch {}
    setGenerating(null);
  };

  const handleUpdateOcc = async () => {
    if (!editOcc) return;
    await updateEventOccurrence(ev.id, editOcc.id, { status: editOcc.status, notes: editOcc.notes ?? undefined });
    setEditOcc(null);
    await loadOcc();
  };

  const upcoming = occurrences.filter(o => o.status === 'upcoming' || o.status === 'active').sort((a, b) => a.occurrence_date.localeCompare(b.occurrence_date));
  const past = occurrences.filter(o => o.status === 'completed' || o.status === 'cancelled').sort((a, b) => b.occurrence_date.localeCompare(a.occurrence_date));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {launch && launch.enabled && (
        launch.launched ? (
          <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 10, padding: '12px 16px', fontSize: 13, color: '#065f46' }}>
            ✅ Événement lancé — date de départ officielle : <strong>{launch.official_start_date}</strong>
          </div>
        ) : launch.ready ? (
          <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#1d4ed8', marginBottom: 6 }}>
              🔔 Seuil atteint — {launch.selected_count}/{launch.min_participants} participants sélectionnés
            </div>
            <div style={{ fontSize: 13, color: '#334155', marginBottom: 10 }}>
              {launch.can_manage
                ? "Valide la date de départ officielle ci-dessous pour générer le planning."
                : "En attente de validation du lancement par un responsable, un streamer assigné ou l'admin."}
            </div>
            {launch.can_manage && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <input type="date" style={{ ...inputStyle, maxWidth: 180 }} value={launchDate}
                  onChange={e => setLaunchDate(e.target.value)} />
                <Btn onClick={handleLaunch} disabled={launching || !launchDate}>
                  {launching ? 'Lancement…' : '🚀 Valider le lancement'}
                </Btn>
              </div>
            )}
          </div>
        ) : (
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 16px', fontSize: 13, color: '#92400e' }}>
            ⏳ En attente de sélection : <strong>{launch.selected_count}/{launch.min_participants}</strong> participants.
            Aucune occurrence ne sera générée tant que le seuil n'est pas atteint (sélection des candidatures au statut « Active »).
          </div>
        )
      )}
      {/* Règles de récurrence */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <span style={sectionTitle}>Règles de récurrence</span>
          <Btn size="sm" onClick={() => setAddingRec(v => !v)}>+ Ajouter une règle</Btn>
        </div>

        {addingRec && (
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 16, marginBottom: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <div>
                <label style={labelStyle}>Type</label>
                <select style={inputStyle} value={newRec.recurrence_type} onChange={e => setNewRec(r => ({ ...r, recurrence_type: e.target.value }))}>
                  <option value="once">Date unique</option>
                  <option value="weekly">Hebdomadaire</option>
                </select>
              </div>
              {newRec.recurrence_type === 'weekly' && (
                <div>
                  <label style={labelStyle}>Jour</label>
                  <select style={inputStyle} value={newRec.day_of_week} onChange={e => setNewRec(r => ({ ...r, day_of_week: Number(e.target.value) }))}>
                    {DAYS_FR.map((d, i) => <option key={i} value={i}>{d}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
              <div><label style={labelStyle}>{newRec.recurrence_type === 'once' ? 'Date' : 'À partir du'}{condEvent ? ' (optionnel)' : ''}</label><input type="date" style={inputStyle} value={newRec.date_from} onChange={e => setNewRec(r => ({ ...r, date_from: e.target.value }))} />{condEvent && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>Laisser vide : date fixée au lancement (seuil atteint)</div>}</div>
              <div><label style={labelStyle}>Heure début</label><input type="time" style={inputStyle} value={newRec.time_start} onChange={e => setNewRec(r => ({ ...r, time_start: e.target.value }))} /></div>
              <div><label style={labelStyle}>Heure fin</label><input type="time" style={inputStyle} value={newRec.time_end} onChange={e => setNewRec(r => ({ ...r, time_end: e.target.value }))} /></div>
            </div>
            {newRec.recurrence_type === 'weekly' && (
              <div style={{ marginBottom: 10 }}>
                <label style={labelStyle}>Durée</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  {(['indefinite', 'weeks', 'date'] as const).map(m => (
                    <button key={m} onClick={() => setNewRec(r => ({ ...r, end_mode: m }))}
                      style={{ flex: 1, padding: '6px 0', borderRadius: 7, border: `1px solid ${newRec.end_mode === m ? '#2563eb' : '#e2e8f0'}`, background: newRec.end_mode === m ? '#eff6ff' : '#fff', color: newRec.end_mode === m ? '#2563eb' : '#64748b', fontSize: 11, cursor: 'pointer', fontWeight: newRec.end_mode === m ? 700 : 400 }}>
                      {m === 'indefinite' ? 'Indéfini' : m === 'weeks' ? 'N semaines' : 'Date fin'}
                    </button>
                  ))}
                </div>
                {newRec.end_mode === 'weeks' && <input type="number" min={1} style={{ ...inputStyle, marginTop: 6 }} placeholder="Ex: 3" value={newRec.weeks_count} onChange={e => setNewRec(r => ({ ...r, weeks_count: e.target.value }))} />}
                {newRec.end_mode === 'date' && <input type="date" style={{ ...inputStyle, marginTop: 6 }} value={newRec.date_until} onChange={e => setNewRec(r => ({ ...r, date_until: e.target.value }))} />}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn size="sm" onClick={handleAddRec} disabled={!newRec.date_from && !condEvent}>Ajouter</Btn>
              <BtnOutline size="sm" onClick={() => setAddingRec(false)}>Annuler</BtnOutline>
            </div>
          </div>
        )}

        {recurrences.length === 0 && !addingRec && (
          <div style={{ color: '#94a3b8', fontSize: 13, padding: '16px 0' }}>Aucune règle de récurrence. Ajoutez-en une pour définir les dates de l'événement.</div>
        )}
        {recurrences.map(rec => (
          <div key={rec.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 6 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
                {rec.recurrence_type === 'once'
                  ? `📅 ${rec.date_from ? fmtDate(rec.date_from) : 'date au lancement'}`
                  : `🔁 Chaque ${DAYS_FR[rec.day_of_week ?? 0]}${rec.date_from ? ` · à partir du ${fmtDate(rec.date_from)}` : ' · date fixée au lancement'}`}
              </div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                {rec.time_start} → {rec.time_end}
                {rec.date_until && ` · jusqu'au ${fmtDate(rec.date_until)}`}
                {rec.weeks_count && ` · ${rec.weeks_count} semaine${rec.weeks_count > 1 ? 's' : ''}`}
                {!rec.date_until && !rec.weeks_count && rec.recurrence_type === 'weekly' && ' · indéfiniment'}
              </div>
            </div>
            <Btn size="sm" color="#16a34a" onClick={() => handleGenerate(rec.id)} disabled={generating === rec.id}>{generating === rec.id ? '…' : '⚡ Générer'}</Btn>
            <BtnOutline size="sm" onClick={() => handleDeleteRec(rec.id)} style={{ color: '#dc2626', borderColor: '#fecaca' }}>✕</BtnOutline>
          </div>
        ))}
      </div>

      {/* Occurrences */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <span style={sectionTitle}>Occurrences ({occurrences.length})</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <BtnOutline size="sm" onClick={loadOcc}>↺ Rafraîchir</BtnOutline>
            {occurrences.length > 0 && (
              <BtnOutline size="sm" style={{ color: '#dc2626', borderColor: '#fecaca' }}
                onClick={async () => {
                  if (!window.confirm(`Supprimer les ${occurrences.length} occurrence(s) ? (utile avant de configurer un démarrage conditionné)`)) return;
                  try { await clearEventOccurrences(ev.id); await loadOcc(); await loadLaunch(); } catch {}
                }}>🗑 Tout supprimer</BtnOutline>
            )}
          </div>
        </div>

        {loadingOcc && <div style={{ color: '#94a3b8', fontSize: 13 }}>Chargement…</div>}
        {!loadingOcc && occurrences.length === 0 && <div style={{ color: '#94a3b8', fontSize: 13 }}>Aucune occurrence — cliquez sur ⚡ Générer pour en créer.</div>}

        {upcoming.length > 0 && (
          <>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 6 }}>À VENIR</div>
            {upcoming.map(o => <OccurrenceRow key={o.id} occ={o} eventId={ev.id} onEdit={setEditOcc} onUpdated={loadOcc} />)}
          </>
        )}
        {past.length > 0 && (
          <>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', marginTop: 12, marginBottom: 6 }}>PASSÉS</div>
            {past.map(o => <OccurrenceRow key={o.id} occ={o} eventId={ev.id} onEdit={setEditOcc} onUpdated={loadOcc} />)}
          </>
        )}
      </div>

      {/* Modal edit occurrence */}
      {editOcc && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={e => { if (e.target === e.currentTarget) setEditOcc(null); }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 400 }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Modifier l'occurrence du {fmtDate(editOcc.occurrence_date)}</div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Statut</label>
              <select style={inputStyle} value={editOcc.status} onChange={e => setEditOcc(o => o && ({ ...o, status: e.target.value as any }))}>
                {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Notes</label>
              <textarea style={{ ...inputStyle, minHeight: 60 }} value={editOcc.notes ?? ''} onChange={e => setEditOcc(o => o && ({ ...o, notes: e.target.value }))} />
            </div>
            <input type="file" ref={occImgRef} accept="image/*" style={{ display: 'none' }} onChange={async e => {
              const file = e.target.files?.[0]; if (!file) return;
              await uploadOccurrenceImage(ev.id, editOcc.id, file);
              await loadOcc();
              e.target.value = '';
            }} />
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <BtnOutline size="sm" onClick={() => occImgRef.current?.click()}>📷 Image spécifique</BtnOutline>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn onClick={handleUpdateOcc}>Sauvegarder</Btn>
              <BtnOutline onClick={() => setEditOcc(null)}>Annuler</BtnOutline>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function OccurrenceRow({ occ, eventId, onEdit, onUpdated }: { occ: EventOccurrence; eventId: number; onEdit: (o: EventOccurrence) => void; onUpdated: () => void }) {
  const stColor = STATUS_COLOR[occ.status] || '#94a3b8';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#fff', border: '1px solid #e2e8f0', borderLeft: `3px solid ${stColor}`, borderRadius: 8, marginBottom: 4 }}>
      {occ.image_url && <img src={occ.image_url} alt="" style={{ width: 32, height: 32, borderRadius: 4, objectFit: 'cover', flexShrink: 0 }} />}
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a' }}>{fmtDateTime(occ.occurrence_date, occ.time_start)} → {occ.time_end}</div>
        {occ.notes && <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>{occ.notes}</div>}
      </div>
      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20, background: stColor + '18', color: stColor }}>{STATUS_LABEL[occ.status]}</span>
      <BtnOutline size="sm" onClick={() => onEdit(occ)}>✏️</BtnOutline>
      <BtnOutline size="sm" style={{ color: '#dc2626', borderColor: '#fecaca' }}
        onClick={async () => { if (window.confirm('Supprimer cette occurrence ?')) { try { await deleteEventOccurrence(eventId, occ.id); onUpdated(); } catch {} } }}>🗑</BtnOutline>
    </div>
  );
}

// ── Onglet Formulaire ────────────────────────────────────────

const FIELD_TYPES = [
  { type: 'section', label: '🗂 Section' },
  { type: 'text', label: '📝 Texte court' },
  { type: 'textarea', label: '📄 Texte long' },
  { type: 'tiktok', label: '🔎 Compte TikTok' },
  { type: 'checkbox', label: '☑️ Cases à cocher' },
  { type: 'radio', label: '⊙ Boutons radio' },
  { type: 'select', label: '▾ Liste déroulante' },
  { type: 'file', label: '📎 Fichier Dropbox' },
];

function parseOpts(j?: string | null): string[] {
  try { const v = JSON.parse(j || '[]'); return Array.isArray(v) ? v : []; } catch { return []; }
}

function FormulaireTab({ ev }: { ev: LiveEvent }) {
  const [fields, setFields] = useState<EventFormField[]>([]);
  const [loading, setLoading] = useState(true);
  const [editField, setEditField] = useState<EventFormField | null>(null);
  const [preview, setPreview] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await getEventFormFields(ev.id); setFields(r.data); } catch {}
    setLoading(false);
  }, [ev.id]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (type: string) => {
    const def = FIELD_TYPES.find(f => f.type === type)?.label.replace(/^[^ ]+ /, '')
      ?? (type === 'section' ? 'Nouvelle section' : 'Champ');
    await addEventFormField(ev.id, { field_type: type, label: def, required: false });
    await load();
  };

  const handleDelete = async (fid: number) => {
    await deleteEventFormField(ev.id, fid);
    setFields(prev => prev.filter(f => f.id !== fid));
  };

  const handleMove = async (idx: number, dir: -1 | 1) => {
    const newFields = [...fields];
    const to = idx + dir;
    if (to < 0 || to >= newFields.length) return;
    [newFields[idx], newFields[to]] = [newFields[to], newFields[idx]];
    setFields(newFields);
    await reorderEventFormFields(ev.id, newFields.map(f => f.id));
  };

  const handleSaveField = async (f: EventFormField) => {
    await updateEventFormField(ev.id, f.id, {
      label: f.label, required: f.required, options_json: f.options_json,
      dropbox_folder: f.dropbox_folder, placeholder: f.placeholder,
      default_value: f.default_value,
    });
    setEditField(null);
    await load();
  };

  if (loading) return <div style={{ color: '#94a3b8', fontSize: 13 }}>Chargement…</div>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ ...(sectionTitle as any), margin: 0, flex: 1 }}>Builder de formulaire</div>
        <BtnOutline size="sm" onClick={() => setPreview(true)}>👁 Prévisualiser</BtnOutline>
        <BtnOutline size="sm" onClick={() => setTplOpen(true)}>📁 Modèles</BtnOutline>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 20 }}>
        {FIELD_TYPES.map(ft => (
          <BtnOutline key={ft.type} size="sm" onClick={() => handleAdd(ft.type)}>+ {ft.label}</BtnOutline>
        ))}
      </div>

      {fields.length === 0 && <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: '32px 0' }}>Aucun champ — ajoutez-en un ci-dessus.</div>}
      {fields.map((f, idx) => {
        const isSection = f.field_type === 'section';
        return (
          <div key={f.id} style={{
            display: 'flex', gap: 8, alignItems: 'flex-start',
            padding: isSection ? '12px 14px' : '10px 12px',
            background: isSection ? '#f5f3ff' : '#fff',
            border: `1px solid ${isSection ? '#ddd6fe' : '#e2e8f0'}`,
            borderLeft: isSection ? '4px solid #7c3aed' : '1px solid #e2e8f0',
            borderRadius: 8, marginBottom: 6,
            marginTop: isSection && idx > 0 ? 16 : 0,
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: isSection ? 14 : 13, fontWeight: 700, color: isSection ? '#6d28d9' : '#0f172a' }}>
                {isSection ? `🗂 ${f.label}` : f.label}
              </div>
              {!isSection && (
                <div style={{ display: 'flex', gap: 6, marginTop: 3, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 10, padding: '1px 6px', background: '#f1f5f9', borderRadius: 20, color: '#64748b' }}>{FIELD_TYPES.find(ft => ft.type === f.field_type)?.label || f.field_type}</span>
                  {!!f.required && <span style={{ fontSize: 10, padding: '1px 6px', background: '#fef3c7', borderRadius: 20, color: '#92400e' }}>Requis</span>}
                  {(f.field_type === 'radio' || f.field_type === 'select' || f.field_type === 'checkbox') && parseOpts(f.options_json).length > 0 && <span style={{ fontSize: 10, color: '#94a3b8' }}>{parseOpts(f.options_json).length} options</span>}
                  {f.placeholder && <span style={{ fontSize: 10, color: '#94a3b8' }}>💬 {f.placeholder}</span>}
                  {f.default_value && <span style={{ fontSize: 10, color: '#16a34a' }}>défaut: {f.default_value}</span>}
                  {f.dropbox_folder && <span style={{ fontSize: 10, color: '#7c3aed' }}>📂 {f.dropbox_folder}</span>}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              <button onClick={() => handleMove(idx, -1)} disabled={idx === 0} style={{ ...smallBtn, opacity: idx === 0 ? 0.3 : 1 }}>↑</button>
              <button onClick={() => handleMove(idx, 1)} disabled={idx === fields.length - 1} style={{ ...smallBtn, opacity: idx === fields.length - 1 ? 0.3 : 1 }}>↓</button>
              <button onClick={() => setEditField(f)} style={{ ...smallBtn }}>✏️</button>
              <button onClick={() => handleDelete(f.id)} style={{ ...smallBtn, color: '#dc2626' }}>🗑</button>
            </div>
          </div>
        );
      })}

      {editField && <FieldEditor field={editField} onSave={handleSaveField} onClose={() => setEditField(null)} />}
      {preview && <PreviewForm fields={fields} onClose={() => setPreview(false)} />}
      {tplOpen && <TemplatesModal ev={ev} hasFields={fields.length > 0} onClose={() => setTplOpen(false)} onApplied={load} />}
    </div>
  );
}

const smallBtn: React.CSSProperties = { background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, padding: '4px 7px', fontSize: 12, cursor: 'pointer' };

function FieldEditor({ field, onSave, onClose }: { field: EventFormField; onSave: (f: EventFormField) => void; onClose: () => void }) {
  const [f, setF] = useState({ ...field });
  const [options, setOptions] = useState<string[]>(() => parseOpts(field.options_json));

  const isSection = f.field_type === 'section';
  const isFile = f.field_type === 'file';
  const hasOptions = f.field_type === 'radio' || f.field_type === 'select' || f.field_type === 'checkbox';
  const hasPlaceholder = f.field_type === 'text' || f.field_type === 'textarea' || f.field_type === 'tiktok';
  const checkboxDefaults = (f.default_value || '').split(',').map(s => s.trim()).filter(Boolean);

  const [fileCfg, setFileCfg] = useState<{ description: string; accept: string; camera: boolean }>(() => {
    try {
      const o = JSON.parse(field.options_json || '{}');
      return { description: o.description || '', accept: o.accept || 'any', camera: !!o.camera };
    } catch { return { description: '', accept: 'any', camera: false }; }
  });
  const cameraEligible = fileCfg.accept === 'image' || fileCfg.accept === 'video';

  const handleSave = () => {
    let oj: string | null = null;
    if (hasOptions) oj = JSON.stringify(options);
    else if (isFile) oj = JSON.stringify({
      description: fileCfg.description || '',
      accept: fileCfg.accept,
      camera: cameraEligible ? fileCfg.camera : false,
    });
    onSave({ ...f, options_json: oj });
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 460, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>
          {isSection ? 'Modifier la section' : 'Modifier le champ'}
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>{isSection ? 'Titre de la section' : 'Label du champ'}</label>
          <input style={inputStyle} value={f.label} onChange={e => setF(x => ({ ...x, label: e.target.value }))} />
        </div>

        {!isSection && (
          <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={!!f.required} onChange={e => setF(x => ({ ...x, required: e.target.checked ? 1 : 0 }))} id="req" />
            <label htmlFor="req" style={{ fontSize: 13, cursor: 'pointer' }}>Champ requis</label>
          </div>
        )}

        {hasPlaceholder && (
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Placeholder (texte d'aide)</label>
            <input style={inputStyle} value={f.placeholder ?? ''} placeholder={f.field_type === 'tiktok' ? '@tonpseudo' : 'Ex : Votre réponse…'} onChange={e => setF(x => ({ ...x, placeholder: e.target.value || null }))} />
          </div>
        )}

        {(f.field_type === 'text' || f.field_type === 'textarea') && (
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Valeur par défaut</label>
            <input style={inputStyle} value={f.default_value ?? ''} onChange={e => setF(x => ({ ...x, default_value: e.target.value || null }))} />
          </div>
        )}

        {hasOptions && (
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Options</label>
            {options.map((opt, i) => (
              <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 5 }}>
                <input style={{ ...inputStyle, flex: 1 }} value={opt} onChange={e => { const n = [...options]; n[i] = e.target.value; setOptions(n); }} />
                <button onClick={() => setOptions(o => o.filter((_, j) => j !== i))} style={{ ...smallBtn, color: '#dc2626' }}>✕</button>
              </div>
            ))}
            <BtnOutline size="sm" onClick={() => setOptions(o => [...o, ''])}>+ Ajouter une option</BtnOutline>
          </div>
        )}

        {(f.field_type === 'radio' || f.field_type === 'select') && options.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Valeur par défaut (présélectionnée)</label>
            <select style={inputStyle} value={f.default_value ?? ''} onChange={e => setF(x => ({ ...x, default_value: e.target.value || null }))}>
              <option value="">— Aucune —</option>
              {options.filter(Boolean).map((o, i) => <option key={i} value={o}>{o}</option>)}
            </select>
          </div>
        )}

        {f.field_type === 'checkbox' && options.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Cases cochées par défaut</label>
            {options.filter(Boolean).map((o, i) => (
              <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 4, cursor: 'pointer' }}>
                <input type="checkbox" checked={checkboxDefaults.includes(o)}
                  onChange={e => {
                    const set = new Set(checkboxDefaults);
                    e.target.checked ? set.add(o) : set.delete(o);
                    setF(x => ({ ...x, default_value: Array.from(set).join(', ') || null }));
                  }} />
                {o}
              </label>
            ))}
          </div>
        )}

        {isFile && (
          <>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Description (consigne affichée au candidat)</label>
              <textarea style={{ ...inputStyle, minHeight: 56 }} placeholder="Ex : Filme-toi 30s en train de présenter ton concept."
                value={fileCfg.description} onChange={e => setFileCfg(c => ({ ...c, description: e.target.value }))} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Type de fichier accepté</label>
              <select style={inputStyle} value={fileCfg.accept}
                onChange={e => setFileCfg(c => ({ ...c, accept: e.target.value, camera: (e.target.value === 'image' || e.target.value === 'video') ? c.camera : false }))}>
                <option value="any">Tous types</option>
                <option value="image">📷 Photo / Image</option>
                <option value="video">🎥 Vidéo</option>
                <option value="audio">🎵 Audio</option>
                <option value="document">📄 Document (PDF, Word…)</option>
              </select>
            </div>
            {cameraEligible && (
              <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" id="cam" checked={fileCfg.camera}
                  onChange={e => setFileCfg(c => ({ ...c, camera: e.target.checked }))} />
                <label htmlFor="cam" style={{ fontSize: 13, cursor: 'pointer' }}>
                  Autoriser la capture {fileCfg.accept === 'image' ? 'photo' : 'vidéo'} via la caméra (mobile / PC)
                </label>
              </div>
            )}
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Dossier Dropbox</label>
              <input style={inputStyle} placeholder="/Candidatures/Photos" value={f.dropbox_folder ?? ''} onChange={e => setF(x => ({ ...x, dropbox_folder: e.target.value || null }))} />
            </div>
          </>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <Btn onClick={handleSave}>Sauvegarder</Btn>
          <BtnOutline onClick={onClose}>Annuler</BtnOutline>
        </div>
      </div>
    </div>
  );
}

// ── Aperçu du formulaire (rendu comme le public) ─────────────
function PreviewForm({ fields, onClose }: { fields: EventFormField[]; onClose: () => void }) {
  type Group = { title: string | null; items: EventFormField[] };
  const groups: Group[] = [];
  let cur: Group = { title: null, items: [] };
  for (const f of fields) {
    if (f.field_type === 'section') {
      if (cur.items.length || cur.title) groups.push(cur);
      cur = { title: f.label, items: [] };
    } else cur.items.push(f);
  }
  if (cur.items.length || cur.title) groups.push(cur);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 210, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 24, overflow: 'auto' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 560 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 700, flex: 1 }}>👁 Aperçu du formulaire</div>
          <button onClick={onClose} style={{ ...smallBtn, fontSize: 16 }}>✕</button>
        </div>
        {groups.length === 0 || fields.length === 0 ? (
          <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: 24 }}>Aucun champ.</div>
        ) : groups.map((g, gi) => (
          <div key={gi} style={{ marginBottom: 18, ...(g.title ? { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 16 } : {}) }}>
            {g.title && <div style={{ fontSize: 14, fontWeight: 700, color: '#6d28d9', marginBottom: 12 }}>{g.title}</div>}
            {g.items.map(f => <PreviewField key={f.id} f={f} />)}
          </div>
        ))}
      </div>
    </div>
  );
}

function PreviewField({ f }: { f: EventFormField }) {
  const opts = parseOpts(f.options_json).filter(Boolean);
  const cbDefaults = (f.default_value || '').split(',').map(s => s.trim()).filter(Boolean);
  const [tk, setTk] = useState('');
  const [tkRes, setTkRes] = useState<{ display_name: string; avatar_url: string | null } | null>(null);
  const [tkLoad, setTkLoad] = useState(false);
  const lbl = <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>{f.label}{!!f.required && <span style={{ color: '#dc2626' }}> *</span>}</label>;

  if (f.field_type === 'text')
    return <div style={{ marginBottom: 14 }}>{lbl}<input style={inputStyle} placeholder={f.placeholder ?? ''} defaultValue={f.default_value ?? ''} /></div>;
  if (f.field_type === 'textarea')
    return <div style={{ marginBottom: 14 }}>{lbl}<textarea style={{ ...inputStyle, minHeight: 70 }} placeholder={f.placeholder ?? ''} defaultValue={f.default_value ?? ''} /></div>;
  if (f.field_type === 'tiktok')
    return (
      <div style={{ marginBottom: 14 }}>{lbl}
        <div style={{ display: 'flex', gap: 6 }}>
          <input style={{ ...inputStyle, flex: 1 }} placeholder={f.placeholder || '@tonpseudo'} value={tk} onChange={e => setTk(e.target.value)} />
          <BtnOutline size="sm" onClick={async () => {
            if (!tk.trim()) return;
            setTkLoad(true);
            try { const r = await resolveTikTokProfile(tk); setTkRes({ display_name: r.data.display_name, avatar_url: r.data.avatar_url }); } catch {}
            setTkLoad(false);
          }}>{tkLoad ? '…' : 'Rechercher'}</BtnOutline>
        </div>
        {tkRes && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, padding: 8, background: '#f8fafc', borderRadius: 8 }}>
            {tkRes.avatar_url && <img src={getProxyImageUrl(tkRes.avatar_url)} alt="" style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover' }} />}
            <div style={{ fontSize: 13, fontWeight: 600 }}>{tkRes.display_name}</div>
          </div>
        )}
      </div>
    );
  if (f.field_type === 'select')
    return <div style={{ marginBottom: 14 }}>{lbl}<select style={inputStyle} defaultValue={f.default_value ?? ''}><option value="">-- Choisir --</option>{opts.map((o, i) => <option key={i} value={o}>{o}</option>)}</select></div>;
  if ((f.field_type === 'radio' || f.field_type === 'checkbox') && opts.length === 0)
    return (
      <div style={{ marginBottom: 14 }}>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13, cursor: 'pointer' }}>
          <input type={f.field_type} defaultChecked={!!f.default_value} style={{ marginTop: 3, flexShrink: 0 }} />
          <span style={{ fontWeight: 600, color: '#334155' }}>{f.label}{!!f.required && <span style={{ color: '#dc2626' }}> *</span>}</span>
        </label>
      </div>
    );
  if (f.field_type === 'radio')
    return <div style={{ marginBottom: 14 }}>{lbl}{opts.map((o, i) => (
      <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 4 }}>
        <input type="radio" name={`pv_${f.id}`} defaultChecked={f.default_value === o} /> {o}
      </label>))}</div>;
  if (f.field_type === 'checkbox')
    return <div style={{ marginBottom: 14 }}>{lbl}{opts.map((o, i) => (
      <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 4 }}>
        <input type="checkbox" defaultChecked={cbDefaults.includes(o)} /> {o}
      </label>))}</div>;
  if (f.field_type === 'file') {
    let fc: any = {}; try { fc = JSON.parse(f.options_json || '{}'); } catch {}
    const tLabel = fc.accept === 'image' ? '📷 Photo' : fc.accept === 'video' ? '🎥 Vidéo' : fc.accept === 'audio' ? '🎵 Audio' : fc.accept === 'document' ? '📄 Document' : '📎 Fichier';
    return (
      <div style={{ marginBottom: 14 }}>{lbl}
        {fc.description && <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6, whiteSpace: 'pre-wrap' }}>{fc.description}</div>}
        <div style={{ border: '1px dashed #cbd5e1', borderRadius: 8, padding: 14, fontSize: 13, color: '#94a3b8', textAlign: 'center' }}>
          {tLabel}{fc.camera ? ' — 📸 capture caméra activée' : ''}{f.dropbox_folder ? ` → ${f.dropbox_folder}` : ''}
        </div>
      </div>
    );
  }
  return null;
}

// ── Modèles de formulaire ────────────────────────────────────
function TemplatesModal({ ev, hasFields, onClose, onApplied }: {
  ev: LiveEvent; hasFields: boolean; onClose: () => void; onApplied: () => void;
}) {
  const [list, setList] = useState<EventFormTemplate[]>([]);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await getEventFormTemplates(); setList(r.data); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try { await saveEventFormTemplate(name.trim(), ev.id); setName(''); await load(); } catch {}
    setBusy(false);
  };
  const apply = async (tid: number) => {
    if (!window.confirm('Remplacer le formulaire actuel par ce modèle ?')) return;
    setBusy(true);
    try { await applyEventFormTemplate(ev.id, tid, true); onApplied(); onClose(); } catch {}
    setBusy(false);
  };
  const del = async (tid: number) => {
    if (!window.confirm('Supprimer ce modèle ?')) return;
    try { await deleteEventFormTemplate(tid); await load(); } catch {}
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 210, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 480, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 700, flex: 1 }}>📁 Modèles de formulaire</div>
          <button onClick={onClose} style={{ ...smallBtn, fontSize: 16 }}>✕</button>
        </div>

        <div style={{ marginBottom: 18, padding: 14, background: '#f8fafc', borderRadius: 10 }}>
          <label style={labelStyle}>Enregistrer le formulaire actuel comme modèle</label>
          <div style={{ display: 'flex', gap: 6 }}>
            <input style={{ ...inputStyle, flex: 1 }} placeholder="Nom du modèle" value={name}
              onChange={e => setName(e.target.value)} disabled={!hasFields} />
            <Btn onClick={save} disabled={busy || !name.trim() || !hasFields}>💾 Enregistrer</Btn>
          </div>
          {!hasFields && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>Ajoutez des champs avant d'enregistrer un modèle.</div>}
        </div>

        <div style={{ ...(sectionTitle as any), marginBottom: 8 }}>Modèles disponibles</div>
        {list.length === 0 && <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: 16 }}>Aucun modèle enregistré.</div>}
        {list.map(t => {
          let n = 0; try { n = JSON.parse(t.fields_json).length; } catch {}
          return (
            <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 6 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{t.name}</div>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>{n} champ{n > 1 ? 's' : ''}</div>
              </div>
              <BtnOutline size="sm" onClick={() => apply(t.id)}>Appliquer</BtnOutline>
              <button onClick={() => del(t.id)} style={{ ...smallBtn, color: '#dc2626' }}>🗑</button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Onglet Candidatures ──────────────────────────────────────

function CandidaturesTab({ ev }: { ev: LiveEvent }) {
  const [apps, setApps] = useState<EventApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [selected, setSelected] = useState<EventApplication | null>(null);
  const [adding, setAdding] = useState(false);
  const [newApp, setNewApp] = useState({ applicant_name: '', applicant_tiktok: '', applicant_email: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await getEventApplications(ev.id); setApps(r.data); } catch {}
    setLoading(false);
  }, [ev.id]);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (app: EventApplication, status: string) => {
    await updateEventApplication(ev.id, app.id, { overall_status: status });
    await load();
  };

  const handleAddApp = async () => {
    if (!newApp.applicant_name.trim()) return;
    await createEventApplication(ev.id, { ...newApp, applicant_tiktok: newApp.applicant_tiktok || null, applicant_email: newApp.applicant_email || null });
    setNewApp({ applicant_name: '', applicant_tiktok: '', applicant_email: '' });
    setAdding(false);
    await load();
  };

  const filtered = filter === 'all' ? apps : apps.filter(a => a.overall_status === filter);
  const STATUS_FILTERS = [['all', 'Toutes'], ['pending', '⏳ En attente'], ['active', '✅ Sélectionnés'], ['eliminated', '❌ Éliminés'], ['finalist', '🥈 Finalistes'], ['winner', '🏆 Gagnants']];

  if (loading) return <div style={{ color: '#94a3b8', fontSize: 13 }}>Chargement…</div>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={sectionTitle}>Candidatures ({apps.length})</span>
        <Btn size="sm" onClick={() => setAdding(v => !v)}>+ Ajouter manuellement</Btn>
      </div>

      {adding && (
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 16, marginBottom: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 10 }}>
            <div><label style={labelStyle}>Nom *</label><input style={inputStyle} value={newApp.applicant_name} onChange={e => setNewApp(n => ({ ...n, applicant_name: e.target.value }))} /></div>
            <div><label style={labelStyle}>TikTok</label><input style={inputStyle} placeholder="@pseudo" value={newApp.applicant_tiktok} onChange={e => setNewApp(n => ({ ...n, applicant_tiktok: e.target.value }))} /></div>
            <div><label style={labelStyle}>Email</label><input type="email" style={inputStyle} value={newApp.applicant_email} onChange={e => setNewApp(n => ({ ...n, applicant_email: e.target.value }))} /></div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn size="sm" onClick={handleAddApp} disabled={!newApp.applicant_name.trim()}>Ajouter</Btn>
            <BtnOutline size="sm" onClick={() => setAdding(false)}>Annuler</BtnOutline>
          </div>
        </div>
      )}

      {/* Filtres */}
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 12 }}>
        {STATUS_FILTERS.map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)}
            style={{ padding: '4px 10px', borderRadius: 20, border: 'none', fontSize: 11, fontWeight: filter === k ? 700 : 400, background: filter === k ? '#0f172a' : '#f1f5f9', color: filter === k ? '#fff' : '#64748b', cursor: 'pointer' }}>
            {l}
          </button>
        ))}
      </div>

      {filtered.length === 0 && <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: 32 }}>Aucune candidature</div>}
      {filtered.map(app => (
        <div key={app.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 5 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{app.applicant_name}</div>
            <div style={{ fontSize: 11, color: '#64748b' }}>{app.applicant_tiktok && `@${app.applicant_tiktok}`}{app.applicant_email && ` · ${app.applicant_email}`}</div>
          </div>
          <select value={app.overall_status} onChange={e => handleStatusChange(app, e.target.value)}
            style={{ padding: '4px 8px', borderRadius: 7, border: '1px solid #e2e8f0', fontSize: 11, cursor: 'pointer', background: '#fff' }}>
            {Object.entries(APP_STATUS_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <BtnOutline size="sm" onClick={async () => { const r = await getEventApplication(ev.id, app.id); setSelected(r.data); }}>Voir</BtnOutline>
        </div>
      ))}

      {selected && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={e => { if (e.target === e.currentTarget) setSelected(null); }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 500, maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>{selected.applicant_name}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 16 }}>{selected.applicant_tiktok && `@${selected.applicant_tiktok} · `}{selected.applicant_email}</div>
            {(selected.answers || []).map(ans => (
              <div key={ans.id} style={{ marginBottom: 10, padding: '8px 12px', background: '#f8fafc', borderRadius: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', marginBottom: 4 }}>{ans.label}</div>
                <div style={{ fontSize: 13, color: '#0f172a' }}>{ans.file_url ? <a href={ans.file_url} target="_blank" rel="noreferrer" style={{ color: '#2563eb' }}>📎 Voir le fichier</a> : ans.answer_text || <em style={{ color: '#94a3b8' }}>Aucune réponse</em>}</div>
              </div>
            ))}
            <BtnOutline onClick={() => setSelected(null)}>Fermer</BtnOutline>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Panneau détail ───────────────────────────────────────────

type DetailTab = 'infos' | 'planning' | 'formulaire';

function EventDetail({ ev, streamers, onUpdated, onDeleted }: {
  ev: LiveEvent; streamers: Streamer[];
  onUpdated: (e: LiveEvent) => void;
  onDeleted: () => void;
}) {
  const [tab, setTab] = useState<DetailTab>('infos');
  const [delConfirm, setDelConfirm] = useState(false);

  const handleDelete = async () => {
    await deleteEvent(ev.id);
    onDeleted();
  };

  const tabs: { id: DetailTab; label: string; concours?: boolean }[] = [
    { id: 'infos', label: '⚙️ Infos' },
    { id: 'planning', label: '📅 Planning' },
    { id: 'formulaire', label: '📋 Formulaire', concours: true },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', flexShrink: 0, padding: '16px 24px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          {ev.image_url && <img src={ev.image_url} alt="" style={{ width: 44, height: 44, borderRadius: 8, objectFit: 'cover' }} />}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.title}</div>
            <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 20, background: (TYPE_COLOR[ev.event_type as keyof typeof TYPE_COLOR] || '#64748b') + '18', color: TYPE_COLOR[ev.event_type as keyof typeof TYPE_COLOR] || '#64748b' }}>
              {TYPE_LABEL[ev.event_type as keyof typeof TYPE_LABEL] || ev.event_type}
            </span>
          </div>
          {delConfirm ? (
            <div style={{ display: 'flex', gap: 6 }}>
              <Btn size="sm" color="#dc2626" onClick={handleDelete}>Confirmer</Btn>
              <BtnOutline size="sm" onClick={() => setDelConfirm(false)}>Annuler</BtnOutline>
            </div>
          ) : (
            <BtnOutline size="sm" onClick={() => setDelConfirm(true)} style={{ color: '#dc2626', borderColor: '#fecaca' }}>🗑 Supprimer</BtnOutline>
          )}
        </div>
        <div style={{ display: 'flex', gap: 0 }}>
          {tabs.filter(t => !t.concours || ev.event_type === 'concours').map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ padding: '8px 16px', background: 'none', border: 'none', borderBottom: `2px solid ${tab === t.id ? '#2563eb' : 'transparent'}`, color: tab === t.id ? '#2563eb' : '#64748b', fontSize: 12, fontWeight: tab === t.id ? 700 : 400, cursor: 'pointer', marginBottom: -1 }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>
      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
        {tab === 'infos' && <InfosTab ev={ev} streamers={streamers} onUpdated={onUpdated} />}
        {tab === 'planning' && <PlanningTab ev={ev} onUpdated={onUpdated} />}
        {tab === 'formulaire' && ev.event_type === 'concours' && <FormulaireTab ev={ev} />}
      </div>
    </div>
  );
}

// ── Formulaire création ──────────────────────────────────────

function CreatePanel({ streamers, onCreate, onCancel }: { streamers: Streamer[]; onCreate: (ev: LiveEvent) => void; onCancel: () => void }) {
  const [form, setForm] = useState({ title: '', description: '', event_type: 'live' });
  const [selStreamers, setSelStreamers] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleCreate = async () => {
    if (!form.title.trim()) { setError('Le titre est requis'); return; }
    setSaving(true);
    try {
      const r = await createEvent({ title: form.title, description: form.description || null, event_type: form.event_type, streamer_ids: selStreamers });
      onCreate(r.data);
    } catch { setError('Erreur lors de la création'); }
    setSaving(false);
  };

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: 32, background: '#f8fafc' }}>
      <div style={{ maxWidth: 560, margin: '0 auto', background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Nouvel événement</div>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#94a3b8' }}>×</button>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Titre *</label>
          <input style={inputStyle} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Nom de l'événement" />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Description</label>
          <textarea style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Description…" />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Type</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {(['live', 'concours'] as const).map(t => (
              <button key={t} onClick={() => setForm(f => ({ ...f, event_type: t }))}
                style={{ flex: 1, padding: '8px 0', borderRadius: 8, border: `2px solid ${form.event_type === t ? TYPE_COLOR[t] : '#e2e8f0'}`, background: form.event_type === t ? TYPE_COLOR[t] + '12' : '#fff', color: form.event_type === t ? TYPE_COLOR[t] : '#64748b', fontSize: 12, fontWeight: form.event_type === t ? 700 : 400, cursor: 'pointer' }}>
                {TYPE_LABEL[t]}
              </button>
            ))}
          </div>
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Streamers</label>
          <StreamerPicker streamers={streamers} selected={selStreamers} onChange={setSelStreamers} />
        </div>
        {error && <div style={{ padding: '8px 12px', background: '#fef2f2', borderRadius: 8, color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn onClick={handleCreate} disabled={saving}>{saving ? 'Création…' : 'Créer l\'événement'}</Btn>
          <BtnOutline onClick={onCancel}>Annuler</BtnOutline>
        </div>
      </div>
    </div>
  );
}

// ── Composant principal ──────────────────────────────────────

export default function EvenementsLive({ isMobile }: { isMobile: boolean }) {
  const [events, setEvents] = useState<LiveEvent[]>([]);
  const [streamers, setStreamers] = useState<Streamer[]>([]);
  const [selected, setSelected] = useState<LiveEvent | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [reqs, setReqs] = useState<EventRequest[]>([]);
  const [reqBusy, setReqBusy] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [er, sr] = await Promise.all([getEvents(), getStreamers()]);
      setEvents(er.data);
      setStreamers(sr.data);
    } catch {}
    setLoading(false);
  }, []);

  const loadReqs = useCallback(async () => {
    try { const r = await getEventRequests(); setReqs(r.data.filter(x => x.status === 'pending')); }
    catch { setReqs([]); }
  }, []);

  useEffect(() => { load(); loadReqs(); }, [load, loadReqs]);

  const acceptReq = async (id: number) => {
    setReqBusy(id);
    try {
      const r = await acceptEventRequest(id);
      await load();
      await loadReqs();
      if (r.data) { setSelected(r.data); setShowCreate(false); }
    } catch (e) { console.error(e); } finally { setReqBusy(null); }
  };
  const rejectReq = async (id: number) => {
    const note = window.prompt('Motif du refus (optionnel) :') ?? undefined;
    setReqBusy(id);
    try { await updateEventRequest(id, { status: 'rejected', admin_notes: note }); await loadReqs(); }
    catch (e) { console.error(e); } finally { setReqBusy(null); }
  };
  const deleteReq = async (id: number) => {
    if (!window.confirm('Supprimer cette demande ?')) return;
    setReqBusy(id);
    try { await deleteEventRequest(id); await loadReqs(); }
    catch (e) { console.error(e); } finally { setReqBusy(null); }
  };

  const filtered = events.filter(ev => {
    if (typeFilter !== 'all' && ev.event_type !== typeFilter) return false;
    if (!search) return true;
    return ev.title.toLowerCase().includes(search.toLowerCase());
  });

  const listPanel = (
    <div style={{ width: isMobile ? '100%' : 320, flexShrink: 0, display: 'flex', flexDirection: 'column', borderRight: isMobile ? 'none' : '1px solid #e2e8f0', height: '100%', background: '#f8fafc' }}>
      <div style={{ padding: '12px 14px', borderBottom: '1px solid #e2e8f0', background: '#fff', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher…" style={{ ...inputStyle, background: '#f8fafc' }} />
        <div style={{ display: 'flex', gap: 6 }}>
          {[['all', 'Tous'], ['live', '📡 Lives'], ['concours', '🏆 Concours']].map(([k, l]) => (
            <button key={k} onClick={() => setTypeFilter(k)} style={{ flex: 1, padding: '5px 0', borderRadius: 6, border: 'none', fontSize: 11, fontWeight: typeFilter === k ? 700 : 400, cursor: 'pointer', background: typeFilter === k ? '#0f172a' : '#f1f5f9', color: typeFilter === k ? '#fff' : '#64748b' }}>{l}</button>
          ))}
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
        {reqs.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>
              Demandes ({reqs.length})
            </div>
            {reqs.map(r => (
              <div key={r.id} style={{ background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 10, padding: '10px 12px', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  {r.requester_avatar
                    ? <img src={getProxyImageUrl(r.requester_avatar)} alt="" onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                    : <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#e9d5ff', flexShrink: 0 }} />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title}</div>
                    <div style={{ fontSize: 11, color: '#7c3aed' }}>{r.requester_name || `#${r.requester_id}`} · {r.event_type === 'concours' ? '🏆 Concours' : '📡 Live'}</div>
                  </div>
                </div>
                {r.description && <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4, whiteSpace: 'pre-wrap' }}>{r.description}</div>}
                {r.desired_schedule && <div style={{ fontSize: 11, color: '#475569' }}>🗓 {r.desired_schedule}</div>}
                {r.desired_prizes && <div style={{ fontSize: 11, color: '#475569', whiteSpace: 'pre-wrap' }}>🏆 {r.desired_prizes}</div>}
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  <button onClick={() => acceptReq(r.id)} disabled={reqBusy === r.id}
                    style={{ flex: 1, padding: '5px 0', borderRadius: 6, border: 'none', background: '#16a34a', color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                    {reqBusy === r.id ? '…' : '✓ Accepter'}
                  </button>
                  <button onClick={() => rejectReq(r.id)} disabled={reqBusy === r.id}
                    style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #f59e0b', background: '#fff', color: '#b45309', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                    Refuser
                  </button>
                  <button onClick={() => deleteReq(r.id)} disabled={reqBusy === r.id}
                    style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #fecaca', background: '#fff', color: '#dc2626', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                    🗑
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        {loading && <div style={{ color: '#94a3b8', textAlign: 'center', padding: 24, fontSize: 13 }}>Chargement…</div>}
        {!loading && filtered.length === 0 && <div style={{ color: '#94a3b8', textAlign: 'center', padding: 32, fontSize: 13 }}><div style={{ fontSize: 32, marginBottom: 10 }}>📅</div>Aucun événement</div>}
        {filtered.map(ev => <EventCard key={ev.id} ev={ev} selected={selected?.id === ev.id && !showCreate} onClick={() => { setSelected(ev); setShowCreate(false); }} />)}
      </div>
      <div style={{ padding: 12, borderTop: '1px solid #e2e8f0', background: '#fff' }}>
        <button onClick={() => { setShowCreate(true); setSelected(null); }} style={{ width: '100%', padding: '10px 0', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          + Créer un événement
        </button>
      </div>
    </div>
  );

  const emptyState = (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 52 }}>📅</div>
      <div style={{ fontSize: 14, fontWeight: 500 }}>Sélectionnez un événement</div>
      <div style={{ fontSize: 13 }}>ou créez-en un nouveau</div>
    </div>
  );

  const detailPanel = selected ? (
    <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <EventDetail
        ev={selected}
        streamers={streamers}
        onUpdated={ev => { setSelected(ev); setEvents(prev => prev.map(e => e.id === ev.id ? ev : e)); }}
        onDeleted={() => { setSelected(null); setEvents(prev => prev.filter(e => e.id !== selected.id)); }}
      />
    </div>
  ) : emptyState;

  const createPanel = (
    <CreatePanel
      streamers={streamers}
      onCreate={ev => { setEvents(prev => [ev, ...prev]); setSelected(ev); setShowCreate(false); }}
      onCancel={() => setShowCreate(false)}
    />
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', background: '#f8fafc' }}>
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', flexShrink: 0, padding: isMobile ? '14px 14px 12px' : '22px 32px 18px' }}>
        <h1 style={{ margin: 0, color: '#0f172a', fontSize: isMobile ? 18 : 22, fontWeight: 700 }}>Événements Live</h1>
        <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 13 }}>Créez et gérez les événements live et concours des streamers</p>
      </div>
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {isMobile ? (
          selected || showCreate ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ padding: '10px 16px', background: '#fff', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
                <button onClick={() => { setSelected(null); setShowCreate(false); }} style={{ border: 'none', background: 'none', color: '#2563eb', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>← Retour</button>
              </div>
              <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>{showCreate ? createPanel : detailPanel}</div>
            </div>
          ) : <div style={{ flex: 1, overflow: 'hidden' }}>{listPanel}</div>
        ) : (
          <>{listPanel}{showCreate ? createPanel : detailPanel}</>
        )}
      </div>
    </div>
  );
}
